import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { MediaTile } from "@/components/MediaTile";
import { MediaLightbox } from "@/components/MediaLightbox";
import { CollectionsRow } from "@/components/CollectionsRow";
import { api, resolveAvatar } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Loader2, Star, ImageIcon, Award, Camera, Eye, MessageSquare, Link as LinkIcon, ExternalLink } from "lucide-react";
import { Badges } from "@/components/Badges";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const linkLabel = (url) => {
  try {
    const h = new URL(url).hostname.replace("www.", "");
    if (h.includes("instagram")) return "Instagram";
    if (h.includes("twitter") || h.includes("x.com")) return "X / Twitter";
    if (h.includes("tiktok")) return "TikTok";
    if (h.includes("onlyfans")) return "OnlyFans";
    return h;
  } catch { return url; }
};

const MemberProfile = () => {
  const { userId } = useParams();
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { setLoading(false); return; }
    setLoading(true);
    api.get(`/profile/${userId}`).then((r) => setP(r.data)).catch(() => toast.error("Member not found")).finally(() => setLoading(false));
    api.get(`/users/${userId}/collections`).then((r) => setCollections(r.data)).catch(() => {});
  }, [userId, user, authLoading]);

  if (authLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen relative z-10">
        <Navbar />
        <div className="max-w-lg mx-auto text-center py-32 px-6">
          <h1 className="font-heading text-3xl mb-3">Member profiles are for members</h1>
          <p className="text-muted-foreground mb-6">Sign in to view profiles, links and send direct messages.</p>
          <Button data-testid="member-signin" onClick={login} className="rounded-full">Sign in</Button>
        </div>
      </div>
    );
  }

  if (!p) return <div className="min-h-screen relative z-10"><Navbar /><div className="text-center py-32 text-muted-foreground">Member not found.</div></div>;

  const stats = [
    { label: "Rank", value: p.rank, icon: Award, big: true },
    { label: "Rated", value: p.ratings_count, icon: Star },
    { label: "Uploads", value: p.uploads_count, icon: ImageIcon },
  ];

  return (
    <div className="min-h-screen relative z-10">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex flex-col sm:flex-row gap-8 items-start">
          <div className="w-40 h-40 rounded-2xl overflow-hidden border border-border bg-secondary shrink-0">
            {resolveAvatar(p.avatar_url) ? <img data-testid="member-avatar" src={resolveAvatar(p.avatar_url)} alt={p.display_name} className="w-full h-full object-cover" />
              : <div className="w-full h-full flex items-center justify-center text-4xl font-heading">{p.display_name?.[0]}</div>}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h1 data-testid="member-name" className="font-heading text-4xl font-light tracking-tighter">{p.display_name}</h1>
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {p.is_admin && <span className="flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border border-primary/40 text-primary bg-primary/10">Owner</span>}
                  {p.role && <span className={cn("flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border", p.role === "contributor" ? "border-primary/40 text-primary bg-primary/10" : "border-border text-muted-foreground")}>{p.role === "contributor" ? <Camera className="w-3 h-3" /> : <Eye className="w-3 h-3" />}{p.role === "contributor" ? "Contributor" : "Viewer"}</span>}
                </div>
              </div>
              {!p.is_me && (
                <Button data-testid="member-message-btn" onClick={() => navigate(`/messages?to=${p.user_id}`)} disabled={!p.dms_enabled && !user.is_admin}
                  className="rounded-full shrink-0"><MessageSquare className="w-4 h-4 mr-1.5" />{(p.dms_enabled || user.is_admin) ? "Message" : "DMs off"}</Button>
              )}
            </div>
            {p.bio && <p className="text-muted-foreground mt-4 leading-relaxed">{p.bio}</p>}
            <Badges badges={p.badges} />

            {/* web links — signed-in members only (this whole page is gated) */}
            {p.links?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4" data-testid="member-links">
                {p.links.map((l, i) => (
                  <a key={i} href={l} target="_blank" rel="noopener noreferrer" data-testid={`member-link-${i}`}
                    className="flex items-center gap-1.5 text-sm rounded-full border border-border bg-secondary/40 px-3 py-1.5 hover:border-primary/50 hover:text-primary transition-colors">
                    <LinkIcon className="w-3.5 h-3.5" />{linkLabel(l)}<ExternalLink className="w-3 h-3 opacity-60" />
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-10">
          {stats.map((s) => (
            <div key={s.label} className="rounded-lg border border-border bg-card p-5">
              <s.icon className="w-5 h-5 text-primary mb-3" />
              <div className={cn("font-semibold", s.big ? "font-heading text-xl" : "font-mono text-3xl")}>{s.value}</div>
              <div className="text-xs tracking-widest uppercase text-muted-foreground mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <h2 className="font-heading text-2xl mb-4">Content <span className="text-muted-foreground text-base">({p.media.length})</span></h2>
          {p.media.length === 0 ? (
            <div className="border border-dashed border-border rounded-xl py-16 text-center text-muted-foreground">Nothing published yet.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {p.media.map((item, i) => <MediaTile key={item.id} item={item} index={i} onOpen={(x) => setSelected(x.id)} />)}
            </div>
          )}
        </div>

        {p.featured && (
          <div className="mt-12">
            <h2 className="font-heading text-2xl mb-4 flex items-center gap-2"><Star className="w-5 h-5 text-primary fill-primary" /> Featured Gallery</h2>
            <div className="max-w-xs"><CollectionsRow collections={[p.featured]} editable={false} /></div>
          </div>
        )}

        {collections.length > 0 && (
          <div className="mt-12">
            <h2 className="font-heading text-2xl mb-4">Galleries <span className="text-muted-foreground text-base">({collections.length})</span></h2>
            <CollectionsRow collections={collections} editable={false} />
          </div>
        )}
      </div>
      <MediaLightbox mediaId={selected} open={!!selected} onClose={() => setSelected(null)} onNavigate={setSelected} playlist={p.media} />
    </div>
  );
};

export default MemberProfile;
